import { create } from 'zustand';
import { resolvePaywallDialogCopy } from '@/config/paywall';
import type { SubscriptionLapseInfo } from '@/types/subscription';

export type PaywallDialogMode = 'standard' | 'reactivate-blocking' | 'reactivate-soft';

interface PaywallDialogStore {
    open: boolean;
    setShowDialog: (show: boolean) => void;
    featureSlug?: string;
    faqHref?: string;
    title?: string;
    description?: string;
    onClose?: () => void;
    isLoading?: boolean;
    setIsLoading?: (loading: boolean) => void;
    actionBeforeRedirectToPlan?: () => void | Promise<void>;
    internalUrlToRedirectOnSuccess?: string;
    /** Hint to the dialog: which copy variant to show. Defaults to 'standard'. */
    mode: PaywallDialogMode;
    /** When non-null, dialog renders the "subscription expired" banner with details. */
    lapseInfo: SubscriptionLapseInfo | null;
    showPaywallDialog: (props: {
        open: boolean;
        featureSlug?: string;
        faqHref?: string;
        internalUrlToRedirectOnSuccess?: string;
        title?: string;
        description?: string;
        onClose?: () => void;
        actionBeforeRedirectToPlan?: () => void | Promise<void>;
        lapseInfo?: SubscriptionLapseInfo | null;
        mode?: PaywallDialogMode;
    }) => void;
}

export const usePaywallDialogStore = create<PaywallDialogStore>((set) => ({
    open: false,
    setShowDialog: (show) => set({ open: show }),
    featureSlug: undefined,
    faqHref: undefined,
    title: resolvePaywallDialogCopy({}).title,
    description: resolvePaywallDialogCopy({}).description,
    onClose: undefined,
    actionBeforeRedirectToPlan: undefined,
    internalUrlToRedirectOnSuccess: undefined,
    mode: 'standard',
    lapseInfo: null,
    showPaywallDialog: ({
        open,
        featureSlug,
        faqHref,
        title,
        description,
        onClose,
        actionBeforeRedirectToPlan,
        internalUrlToRedirectOnSuccess,
        lapseInfo,
        mode,
    }) => {
        const resolvedCopy = resolvePaywallDialogCopy({ featureSlug, title, description });
        const inferredMode: PaywallDialogMode =
            mode ??
            (lapseInfo && lapseInfo.lapseType !== 'NEVER_SUBSCRIBED'
                ? 'reactivate-blocking'
                : 'standard');
        set({
            open,
            featureSlug,
            faqHref,
            title: resolvedCopy.title,
            description: resolvedCopy.description,
            internalUrlToRedirectOnSuccess,
            onClose,
            actionBeforeRedirectToPlan,
            lapseInfo: lapseInfo ?? null,
            mode: inferredMode,
        });
    },
}));
